import { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import toastConfig from "../../../../../components/shared/constants/toaster-config";
import {
  BROADCAST_DELAY_MINUTES,
  BROADCAST_SEND_TYPE,
} from "../constants/broadcast";
import { createBroadcast as createBroadcastUtility } from "../../../../../utils/broadcast";
import { resolveBroadcastErrorMessage } from "../helpers/broadcast-error-messages";

export const useBroadcast = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSentBroadcast, setIsSentBroadcast] = useState(false);
  const [error, setError] = useState(null);

  const channelId = useSelector((state) => state.campaign.create.channelId);
  const waChannels = useSelector(
    (state) => state.channels.data.whatsappChannels,
  );

  const getSendAt = ({ sendType, scheduledDate, scheduledTime, delayTime }) => {
    if (sendType === BROADCAST_SEND_TYPE.NOW) {
      const now = new Date();
      now.setHours(now.getHours());

      const delay = delayTime ? BROADCAST_DELAY_MINUTES : 0;

      now.setMinutes(now.getMinutes() + delay);
      now.setSeconds(0);
      now.setMilliseconds(0);

      return now.toISOString();
    }

    if (
      sendType === BROADCAST_SEND_TYPE.SCHEDULED &&
      scheduledDate &&
      scheduledTime
    ) {
      try {
        const scheduled = new Date(`${scheduledDate}T${scheduledTime}:00.000Z`);
        scheduled.setHours(scheduled.getHours() + 6);

        return scheduled.toISOString();
      } catch (e) {
        return null;
      }
    }

    return null;
  };

  const createBroadcast = async ({
    broadcastName,
    templateName,
    templateFileUrl,
    phonesToNotify,
    sendType,
    scheduledDate,
    scheduledTime,
    contacts,
    actions,
    templateNew,
    tagId,
  }) => {
    const ERROR_MESSAGE = "Error al crear la campaña, contacte a soporte";

    setError(null);

    if (!channelId) {
      setError(ERROR_MESSAGE);
      toast.error(ERROR_MESSAGE, toastConfig);
      return null;
    }

    const currentChannel = waChannels.find((ch) => ch.id === channelId);

    if (!currentChannel || !currentChannel.pageToken) {
      setError(ERROR_MESSAGE);
      toast.error(ERROR_MESSAGE, toastConfig);
      return null;
    }

    if (!broadcastName) {
      const errorMessage = "El nombre de la campaña es obligatorio";
      setError(errorMessage);
      toast.error(errorMessage, toastConfig);
      return null;
    }

    if (!contacts || contacts.length === 0) {
      const errorMessage = "No se encontraron contactos";
      setError(errorMessage);
      toast.error(errorMessage, toastConfig);
      return null;
    }

    const leads = contacts.map((contact) => ({
      phone: contact.phone,
      vars: Object.values(contact.variables || {}),
    }));

    const broadcastData = {
      name: broadcastName,
      templateName: templateName,
      leads: leads,
      token: currentChannel.pageToken,
      actions,
      phonesToNotify,
      sendAt: getSendAt({
        sendType,
        scheduledDate,
        scheduledTime,
        delayTime: templateNew,
      }),
      ...(templateFileUrl && { fileUrl: templateFileUrl }),
      ...(tagId && { tagId }),
    };

    try {
      setIsLoading(true);
      setIsSentBroadcast(false);

      const broadcastResult = await createBroadcastUtility(broadcastData);

      setIsSentBroadcast(true);

      return broadcastResult;
    } catch (error) {
      const { message, isExpected } = resolveBroadcastErrorMessage(error);

      toast.error(message, toastConfig);
      setError(message);

      if (!isExpected) {
        const tags = { component: "BroadcastSelectFile", op: "createBroadcast" };
        if (typeof error?.supportRef === "string" && error.supportRef.trim()) {
          tags.support_ref = error.supportRef;
        }
        Sentry.captureException(error, { tags });
      }

      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createBroadcast,
    isLoading,
    isSentBroadcast,
    error,
  };
};
